import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import {
  Container,
  Dropdown,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Table,
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PosterAvatar } from '../../../components/PosterAvatar';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { useCanAccessLeadership } from '../../../hooks/canAccessLeadership';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import styles from './PostEngagement.module.css';

const GET_POST_ENGAGEMENT = gql`
  query PostEngagementMetrics($days: Int) {
    postEngagementMetrics(days: $days) {
      postId
      title
      type
      publishAt
      poster { id name photoUrl }
      views
      uniqueViewers
      totalDwellMs
      avgDwellMs
      imageTaps
      carouselSwipes
      byPlatform { platform views uniqueViewers avgDwellMs }
      images {
        imageId
        position
        image { id url }
        views
        uniqueViewers
        avgDwellMs
        taps
        fullscreenViews
      }
    }
  }
`;

const WINDOW_OPTIONS = [
  { key: 7, value: 7, text: 'Last 7 days' },
  { key: 30, value: 30, text: 'Last 30 days' },
  { key: 90, value: 90, text: 'Last 90 days' },
  { key: 365, value: 365, text: 'Last year' },
];

type SortKey = 'recent' | 'views' | 'uniqueViewers' | 'avgDwellMs';

const SORT_OPTIONS: { key: SortKey; value: SortKey; text: string }[] = [
  { key: 'recent', value: 'recent', text: 'Most recent' },
  { key: 'views', value: 'views', text: 'Most views' },
  { key: 'uniqueViewers', value: 'uniqueViewers', text: 'Most unique viewers' },
  { key: 'avgDwellMs', value: 'avgDwellMs', text: 'Longest average watch' },
];

/** "1m 12s" rather than a raw millisecond count — nobody reads 72000 as over a minute. */
const formatDuration = (ms: number): string => {
  if (!ms) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

const Stat = ({ value, label }: { value: React.ReactNode; label: string }) => (
  <div className={styles.stat}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

const ImageBreakdown = ({ images }: { images: any[] }) => {
  if (images.length === 0) {
    return <div className={styles.noMedia}>This post has no photos or video.</div>;
  }

  return (
    <>
      <Table basic="very" compact unstackable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Image</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Views</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Unique</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Avg watch</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Taps</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Fullscreen</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {images.map((img) => (
            <Table.Row key={img.imageId}>
              <Table.Cell>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em' }}>
                  {img.image?.url ? (
                    <img src={img.image.url} alt="" className={styles.thumb} />
                  ) : (
                    <div className={styles.thumbPlaceholder} />
                  )}
                  <span className={styles.postMeta}>
                    #{(img.position ?? 0) + 1}
                  </span>
                </div>
              </Table.Cell>
              <Table.Cell textAlign="right">{img.views}</Table.Cell>
              <Table.Cell textAlign="right">{img.uniqueViewers}</Table.Cell>
              <Table.Cell textAlign="right">{formatDuration(img.avgDwellMs)}</Table.Cell>
              <Table.Cell textAlign="right">{img.taps}</Table.Cell>
              <Table.Cell textAlign="right">{img.fullscreenViews}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {/* Without this, #1 looks like the runaway favourite on every post ever published. */}
      <div className={styles.caption}>
        Image #1 is counted on every view of the post — it is the photo the carousel opens on.
        Compare it with other #1s, not with the images beside it.
      </div>
    </>
  );
};

const PostRow = ({ post }: { post: any }) => {
  const [expanded, setExpanded] = useState(false);
  const published = parseApiDate(post.publishAt);

  return (
    <div className={styles.postRow}>
      <button
        type="button"
        className={styles.postHeader}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <Icon name={expanded ? 'chevron down' : 'chevron right'} color="grey" />
        <PosterAvatar photoUrl={post.poster?.photoUrl} />
        <div>
          <div className={styles.postTitle}>{post.title || 'Untitled post'}</div>
          <div className={styles.postMeta}>
            {post.poster?.name ?? 'Unknown'}
            {published ? ` · ${published.toLocaleDateString()}` : ''}
            {post.byPlatform.length > 0 && ' · '}
            {post.byPlatform
              .map((p: any) => `${PLATFORM_LABELS[p.platform] ?? p.platform} ${p.views}`)
              .join(' · ')}
          </div>
        </div>

        <div className={styles.statStrip}>
          <Stat value={post.views} label="Views" />
          <Stat value={post.uniqueViewers} label="People" />
          <Stat value={formatDuration(post.avgDwellMs)} label="Avg watch" />
          <Stat value={post.imageTaps} label="Taps" />
          <Stat value={post.carouselSwipes} label="Swipes" />
        </div>
      </button>

      {expanded && (
        <div className={styles.expanded}>
          <ImageBreakdown images={post.images} />
        </div>
      )}
    </div>
  );
};

export const PostEngagementView: React.FC = () => {
  // Both hooks read the same GET_ME query, so they resolve together off one request. The
  // `Gate` variant is the one that can tell "not a reviewer" from "GET_ME hasn't answered
  // yet" — redirecting on the plain hook bounces a real reviewer during the initial load.
  const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
  const canAccessLeadership = useCanAccessLeadership();

  const [days, setDays] = useState(30);
  const [sortKey, setSortKey] = useState<SortKey>('recent');

  const { data, loading, error } = useQuery(GET_POST_ENGAGEMENT, {
    variables: { days },
    fetchPolicy: 'network-only',
  });

  const posts = useMemo(() => {
    const rows = [...(data?.postEngagementMetrics ?? [])];
    if (sortKey === 'recent') return rows; // already newest-first from the server
    return rows.sort((a: any, b: any) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
  }, [data, sortKey]);

  // Mirrors the server's two-role gate: leadership *or* content reviewer.
  if (!meLoading && !isContentReviewer && !canAccessLeadership) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  return (
    <ResponsiveContainer>
      <Container className={styles.pageContainer}>
        <div className={styles.headerSection}>
          <Header as="h1" style={{ margin: 0 }}>Post engagement</Header>
          {!loading && !error && (
            <Label color="blue" size="tiny">{posts.length} posts</Label>
          )}
        </div>

        <div className={styles.controls}>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Period</span>
            <Dropdown
              selection
              compact
              options={WINDOW_OPTIONS}
              value={days}
              onChange={(_, { value }) => setDays(value as number)}
            />
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Sort</span>
            <Dropdown
              selection
              compact
              options={SORT_OPTIONS}
              value={sortKey}
              onChange={(_, { value }) => setSortKey(value as SortKey)}
            />
          </div>
        </div>

        {loading && (
          <Loader active inline="centered" style={{ marginTop: '4em' }}>
            Loading engagement…
          </Loader>
        )}

        {error && <Message error content="Could not load post engagement." />}

        {!loading && !error && posts.length === 0 && (
          <Message
            info
            content="No posts published in this period yet — or nobody has opened the app since engagement tracking shipped."
          />
        )}

        {!loading && !error && posts.map((post: any) => (
          <PostRow key={post.postId} post={post} />
        ))}

        {!loading && !error && posts.length > 0 && (
          <div className={styles.caption}>
            <p>
              A view is counted when at least half the post is on screen for a full second, so
              scrolling quickly past does not count. The same person is counted each time they
              come back; “People” counts them once.
            </p>
            {/* No code can fix this one, so it has to be said. During a rollout the iOS and
                Android columns measure update adoption as much as reading habits. */}
            <p>
              <strong>Only app versions that include engagement tracking report anything.</strong>{' '}
              While a new release is still rolling out, these numbers count part of your
              community rather than all of it, and the iOS and Android split partly reflects how
              fast each store's update has reached people. Treat the totals as a floor, and hold
              off comparing the two platforms until both releases have settled.
            </p>
          </div>
        )}
      </Container>
    </ResponsiveContainer>
  );
};
